import {HttpErrorResponse} from '@angular/common/http';
import {filter, firstValueFrom, map} from 'rxjs';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {MmProjectLoaderService} from '../mm-project/mm-project-loader.service';
import {ProjectService} from '@mydocma/project';
import {Injectable} from '@angular/core';

@Injectable()
@UntilDestroy()
export class ErrorService {

  fieldNamesByProp: {[prop: string]: string} = null;
  fieldNamesByDefaultName: {[prop: string]: string} = null;

  constructor(private readonly projectLoaderService: MmProjectLoaderService,
              private readonly projectService: ProjectService) {

    this.projectService.currentProject.pipe(untilDestroyed(this))
      .subscribe(async project => {
        await this.initFields();
      });
  }

  async initFields() {
    this.fieldNamesByProp = Object.fromEntries(await firstValueFrom(this.projectLoaderService.fields
      .pipe(
        filter(f => f.loadState.status === LoadStatusEnum.Loaded),
        map(m => {
          return m.data.filter(x => x.Id != null).map(x => [x.Prop, (x.Project ?? x.Global)]);
        }),
      )));

    this.fieldNamesByDefaultName = Object.fromEntries(await firstValueFrom(this.projectLoaderService.fields
      .pipe(
        filter(f => f.loadState.status === LoadStatusEnum.Loaded),
        map(m => {
          return m.data.filter(x => x.Id != null).map(x => [x.Id, (x.Project ?? x.Global)]);
        }),
      )));
  }

  getError(error: any) {
    let jsonError: any;
    const getHttpError = () => `${(error as HttpErrorResponse).statusText} (${(error as HttpErrorResponse).status})`;

    // Now you can safely use "this.fieldNamesByProp" here.
    if (error instanceof HttpErrorResponse) {
      try {
        if (error.error) {
          if (error.error instanceof Object) {
            if ((error.error?.InternalErrors?.length ?? 0) > 0) {
              for (const errorKey in error.error?.InternalErrors) {
                const fieldName = this.fieldNamesByDefaultName?.[error.error.InternalErrors[errorKey].Prop]
                  ?? this.fieldNamesByProp?.[error.error.InternalErrors[errorKey].Prop];
                if (fieldName) {
                  error.error.InternalErrors[errorKey].Message = error.error.InternalErrors[errorKey].Message.replaceAll(`'${error.error.InternalErrors[errorKey].Prop}'`, `'${fieldName}'`);
                }
              }
              return error.error;
            } else if (error.error.Code && error.error.Message) {
              return error.error.Message;
            } else {
              return getHttpError();
            }
          } else {
            jsonError = JSON.parse(error.error);
            if ((jsonError?.InternalErrors?.length ?? 0) > 0) {
              return jsonError;
            }
          }
        } else if (error.message) {
          return error.message;
        }
      } catch {
        return (error?.error ? error.error : getHttpError());
      }
    } else if (error instanceof Error) {
      return error.message;
    } else {
      try {
        if (error instanceof Object && (error?.InternalErrors?.length ?? 0) > 0) {
          for (const errorKey in error.InternalErrors) {
            const fieldName = this.fieldNamesByDefaultName?.[error.InternalErrors[errorKey].Prop]
              ?? this.fieldNamesByProp?.[error.InternalErrors[errorKey].Prop];
            if (fieldName) {
              error.InternalErrors[errorKey].Message = error.InternalErrors[errorKey].Message.replaceAll(`'${error.InternalErrors[errorKey].Prop}'`, `'${fieldName}'`);
            }
          }
          return error;
        } else {
          jsonError = error ? JSON.parse(error) : null;
          if ((jsonError?.InternalErrors?.length ?? 0) > 0) {
            return jsonError;
          }
        }
      } catch {
        return error;
      }
    }

    return error;
  }

  getErrorMessage(error: any) {
    const errorObj = this.getError(error);
    return ((errorObj?.InternalErrors?.length ?? 0) > 0 && errorObj?.InternalErrors[0]?.Message
      ? errorObj.InternalErrors[0].Message
      : error);
  }
}
